import React from 'react';
import { Text, Title, Box, NumberInput, Stack, Group, Table, Button } from '@mantine/core';
import { useMLSettings, useUpdateMLSettings } from '../../../hooks/useSettings';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconDeviceFloppy } from '@tabler/icons';

const DEFAULT_BUSINESS_CATEGORIES = [
  { key: 'call', label: 'Call' },
  { key: 'ticket', label: 'Support Ticket' },
  { key: 'survey', label: 'Survey' },
  { key: 'chat', label: 'Chat/Conversation channel' },
  { key: 'churn', label: 'Churn/Lost Reason' },
  { key: 'note', label: 'Account Note' },
  { key: 'request', label: 'Request' },
  { key: 'account', label: 'Account' },
  { key: 'opportunity', label: 'Opportunity' },
];

export const BusinessCategoriesTab: React.FC = () => {
  const { data: mlSettings } = useMLSettings();
  const { mutate: updateSettings } = useUpdateMLSettings();

  // Track loading state for each field
  const [loadingFields, setLoadingFields] = React.useState<Record<string, boolean>>({});
  // Track temporary values before applying
  const [tempValues, setTempValues] = React.useState<Record<string, number>>({});
  
  const businessCategoryDaysWindow = mlSettings?.businessCategoryDaysWindow || {};
  const [defaultDaysValue, setDefaultDaysValue] = React.useState(businessCategoryDaysWindow?.default || 180);

  const handleUpdate = async (
    categoryKey: string, 
    daysValue: number
  ) => {
    if(loadingFields[categoryKey]) return;
    
    setLoadingFields(prev => ({ ...prev, [categoryKey]: true }));

    const updatedBusinessCategories = {
      ...businessCategoryDaysWindow,
      [categoryKey]: daysValue
    };

    updateSettings(
      {
        businessCategoryDaysWindow: updatedBusinessCategories
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Success',
            message: `${categoryKey} setting updated successfully`,
            color: 'green',
          });
          setLoadingFields(prev => ({ ...prev, [categoryKey]: false }));
        },
        onError: (error) => {
          notifications.show({
            title: 'Error',
            message: 'Failed to update ML settings',
            color: 'red',
          });
          setLoadingFields(prev => ({ ...prev, [categoryKey]: false }));
        },
      }
    );
  };

  // Function to save only the default value without applying to categories
  const saveDefaultValue = () => {
    const updatedBusinessCategories = { 
      ...businessCategoryDaysWindow,
      default: defaultDaysValue 
    };

    updateSettings(
      {
        businessCategoryDaysWindow: updatedBusinessCategories
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Success',
            message: 'Default value saved',
            color: 'green',
          });
        },
        onError: (error) => {
          notifications.show({
            title: 'Error',
            message: 'Failed to save default value',
            color: 'red',
          });
        },
      }
    );
  };

  return (
    <Stack spacing="xs">
      <Box>
        <Title py={0} order={4}>Business Category Time Windows (Days)</Title>
        <Text size="sm">The time window for each business category. The default is 30 days.</Text>
      </Box>
      
      <Group position="apart" mt="xs">
        <Text size="sm">Set default for all categories:</Text>
        <Group spacing="xs">
          <NumberInput
            min={1}
            value={defaultDaysValue}
            onChange={(value) => setDefaultDaysValue(value)}
            styles={{
              input: {
                width: '100px'
              }
            }}
          />
          <Button h={36} size="xs" onClick={saveDefaultValue} loading={loadingFields['default']}>
            Save Default
          </Button>
        </Group>
      </Group>

      <Table striped highlightOnHover withBorder>
        <thead>
          <tr>
            <th>Category</th>
            <th style={{ textAlign: 'right' }}>Days</th>
            <th style={{ width: '80px' }}></th>
          </tr>
        </thead>
        <tbody>
          {DEFAULT_BUSINESS_CATEGORIES.map((category) => (
            <tr key={category.key}>
              <td>{category.label}</td>
              <td style={{ textAlign: 'right', justifyItems: 'right' }}>
                <NumberInput
                  min={1}
                  value={tempValues[category.key] || businessCategoryDaysWindow[category.key] || defaultDaysValue}
                  onChange={(value) => setTempValues(prev => ({ ...prev, [category.key]: value || defaultDaysValue }))}
                  disabled={loadingFields[category.key]}
                  styles={{
                    input: {
                      width: '100px'
                    }
                  }}
                />
              </td>
              <td>
                <Button 
                  size="xs" 
                  onClick={() => handleUpdate(category.key, tempValues[category.key])}
                  loading={loadingFields[category.key]}
                  leftIcon={businessCategoryDaysWindow[category.key] !== undefined ? <IconDeviceFloppy size={14} /> : <IconPlus size={14} />} 
                >
                  {businessCategoryDaysWindow[category.key] !== undefined ? 'Update' : 'Add'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Stack>
  );
};

export default BusinessCategoriesTab; 